/**
 * Дата сохранённого вида ОТНОСИТЕЛЬНА, а не абсолютна.
 *
 * Вид — это привычка смотреть: «завтрашний прогноз по сайре в моём районе».
 * Сохранённая абсолютная дата превращает такую привычку в снимок: через неделю
 * тот же вид открывает поле недельной давности, а карта поверх него — свежие
 * тайлы, которых на ту дату уже нет. Поэтому храним СМЕЩЕНИЕ ко дню сохранения
 * и восстанавливаем дату от сегодняшнего дня: вид, сохранённый на завтра,
 * завтрашним и открывается всегда.
 *
 * Смещение и есть весь параметр, который вид пересчитывает при открытии.
 * Остальное — слой, глубина, наложения, рамка — восстанавливается как было.
 *
 * СТАРЫЕ ВИДЫ смещения не несут, но несут дату и день сохранения — и это не
 * догадка, а ровно то же самое, записанное иначе: разница между ними и есть
 * смещение, которое человек тогда выбрал.
 */
import { dateFromOffset, dayOffset } from '../models/driftDay'

/** Ключ смещения в `url_state`. Дата рядом остаётся: по ней открываются виды
 *  в витринах, которые про смещение ещё не знают, и она же читается глазами
 *  при разборе полётов. */
export const OFFSET_KEY = 'date_offset'

/** Смещение даты вида ко дню сохранения (сутки, может быть отрицательным). */
export function savedDateOffset(date: string | null | undefined, savedOn: string): number | null {
  if (!date) return null
  const offset = dayOffset(date, savedOn)
  return Number.isFinite(offset) ? offset : null
}

function asOffset(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isInteger(n) ? n : null
}

/** 'YYYY-MM-DD' из ISO-момента (`created_at` приходит с временем). */
function dayOf(iso: string | null | undefined): string | null {
  if (!iso) return null
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(iso)
  return m ? m[1] : null
}

/**
 * На какую дату открыть сохранённый вид.
 *
 * Порядок: своё смещение → смещение, выведенное из даты и дня сохранения
 * (старые виды) → сохранённая дата как есть (день сохранения неизвестен —
 * выдумывать смещение не из чего) → null (даты в виде не было вовсе).
 */
export function savedViewDate(
  urlState: Record<string, unknown>,
  createdAt: string | null | undefined,
  today: string,
): string | null {
  const offset = asOffset(urlState[OFFSET_KEY])
  if (offset !== null) return dateFromOffset(offset, today)

  const saved = typeof urlState.date === 'string' && urlState.date ? urlState.date : null
  const savedOn = dayOf(createdAt)
  if (saved && savedOn) {
    const derived = savedDateOffset(saved, savedOn)
    if (derived !== null) return dateFromOffset(derived, today)
  }
  return saved
}
